import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useUser from '@hooks/auth/useUser';
import useAppCustom from '@hooks/useAppCustom';
import { IconProps } from '@models/icons';
import type { RegisteredActivityName } from '@stackflow/config';
import { Link } from '@stackflow/link';
import { pressActionEffect } from '@styles/mixin';
import { JSX } from 'react';

interface Props {
    label: string;
    activityName?: RegisteredActivityName;
    activityParams: object;
    icon: (props: IconProps) => JSX.Element;
    isCenter?: boolean;
    isCurrent?: boolean;
    useGuard?: boolean;
}

function NavItem({
    label, icon, isCenter, isCurrent, activityName, activityParams, useGuard,
}:Props) {
    const IconComponent = icon;
    const { main } = useAppCustom();
    const navigationCenterIcon = main?.navigationCenterIcon;
    const { user } = useUser();
    const guarded = useGuard && !user;
    if (activityName) {
        return (
            <Link css={containerCss}
                activityName={guarded ? 'SignInActivity' : activityName}
                // @ts-ignore
                activityParams={guarded ? {} : activityParams} >
                {isCenter ? (
                    <CenterButton withImage={!!navigationCenterIcon}>
                        {navigationCenterIcon ? (
                            <Image src={navigationCenterIcon}
                                width={72}
                                height={72} />
                        ) : (
                            <IconComponent size={32}
                                color='white'/>
                        )}
                    </CenterButton>
                ) : (
                    <IconComponent size={24}
                        color={isCurrent ? colors.gray7 : colors.gray5}/>
                )}
                <Text size='m'
                    fontWeight='bold'
                    color={isCurrent ? colors.gray9 : colors.gray5}>
                    {label}
                </Text>
            </Link>
        );
    }
    return (
        <Container>
            {isCenter ? (
                <IconComponent size={32}
                    color='white'/>
            ) : (
                <IconComponent size={24}
                    color={isCurrent ? colors.gray7 : colors.gray5}/>
            )}
            <Text size='m'
                fontWeight='bold'
                color={isCurrent ? colors.gray9 : colors.gray5}>
                {label}
            </Text>
        </Container>
    );
}

const containerCss = css`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-end;
    padding: 8px 0;
    position: relative;
    row-gap: 4px;
    z-index: 2;`;

const Container = styled.div`
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-end;
    padding: 8px 0;
    position: relative;
    row-gap: 4px;
    z-index: 2;
`;

const CenterButton = styled.div<{withImage?: boolean}>`
    align-items: center;
    aspect-ratio: 1;
    background-color: var(--color-gray6);
    border: ${(props) => (!props.withImage ? '3px solid var(--color-gray8)' : 'none')};
    border-radius: 50%;
    bottom: 36px;
    display: flex;
    box-shadow:  0 2px 6px rgba(0, 0, 0, 0.14);
    justify-content: center;
    max-width: 72px;
    min-height: 72px;
    position: absolute;
    width: 100%;
    overflow: hidden;
    ${pressActionEffect}
`;

export default NavItem;
